// Home.jsx — Premium gifting homepage with hero slider, occasions, price ranges & product grid

import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { collection, getDocs, query, orderBy, doc, getDoc, setDoc, where, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import { db } from "../firebase/config";
import { seedProducts } from "../firebase/seedProducts";
import ProductCard from "../components/ProductCard";
import HeroSlider from "../components/HeroSlider";
import OccasionCategories from "../components/OccasionCategories";
import PriceRangeSection from "../components/PriceRangeSection";
import TrustBadges from "../components/TrustBadges";

// MUI Icons
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import RedeemOutlinedIcon from "@mui/icons-material/RedeemOutlined";
import ToysOutlinedIcon from "@mui/icons-material/ToysOutlined";
import CasinoOutlinedIcon from "@mui/icons-material/CasinoOutlined";
import BrushOutlinedIcon from "@mui/icons-material/BrushOutlined";
import LocalFireDepartmentOutlinedIcon from "@mui/icons-material/LocalFireDepartmentOutlined";
import StarOutlinedIcon from "@mui/icons-material/StarOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";

// Category config — maps label → MUI icon + gradient
const CATEGORIES = [
  { label: "All", Icon: CardGiftcardOutlinedIcon, color: "from-rose-500 to-pink-600" },
  { label: "Gift Items", Icon: RedeemOutlinedIcon, color: "from-pink-400 to-rose-500" },
  { label: "Toys", Icon: ToysOutlinedIcon, color: "from-yellow-400 to-amber-500" },
  { label: "Games", Icon: CasinoOutlinedIcon, color: "from-blue-400 to-indigo-600" },
  { label: "Handicrafts", Icon: BrushOutlinedIcon, color: "from-emerald-400 to-teal-500" },
];

// ── Section header ─────────────────────────────────────────────────────────
function SectionHeader({ badge, BadgeIcon, title, accentTitle, desc, badgeColor = "text-rose-500 bg-rose-50" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="text-center mb-10"
    >
      <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest mb-3 px-4 py-1.5 rounded-full ${badgeColor}`}>
        {BadgeIcon && <BadgeIcon sx={{ fontSize: 14 }} />}
        {badge}
      </span>
      <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
        {title}{" "}
        {accentTitle && <span className="text-gradient-gift">{accentTitle}</span>}
      </h2>
      {desc && <p className="text-gray-500 text-base sm:text-lg max-w-xl mx-auto">{desc}</p>}
    </motion.div>
  );
}

// ── Product skeleton ───────────────────────────────────────────────────────
function ProductSkeleton() {
  return (
    <div className="bg-white rounded-3xl overflow-hidden border border-gray-100" style={{ boxShadow: "0 2px 16px rgba(0,0,0,0.06)" }}>
      <div className="skeleton" style={{ height: "220px" }} />
      <div className="p-4 space-y-3">
        <div className="skeleton h-3 rounded-full w-1/3" />
        <div className="skeleton h-4 rounded-full w-3/4" />
        <div className="skeleton h-3 rounded-full w-full" />
        <div className="skeleton h-3 rounded-full w-2/3" />
        <div className="flex justify-between items-center pt-2">
          <div className="skeleton h-5 rounded-full w-16" />
          <div className="skeleton h-8 rounded-xl w-24" />
        </div>
      </div>
    </div>
  );
}

// ── Newsletter section ─────────────────────────────────────────────────────
function NewsletterSection() {
  const { currentUser } = useAuth() || {};
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alreadySubscribed, setAlreadySubscribed] = useState(false);

  useEffect(() => {
    async function checkSubscription() {
      if (currentUser?.email) {
        try {
          const emailTrimmed = currentUser.email.trim().toLowerCase();

          // Check by ID first (modern approach)
          const docRef = doc(db, "subscribers", emailTrimmed);
          try {
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
              setAlreadySubscribed(true);
              return;
            }
          } catch (err) {
            console.error("Error reading doc by ID:", err);
          }

          // Fallback: check by email field if ID doesn't match
          try {
            const q = query(
              collection(db, "subscribers"),
              where("email", "==", emailTrimmed)
            );
            const querySnap = await getDocs(q);

            if (!querySnap.empty) {
              setAlreadySubscribed(true);
            } else {
              setAlreadySubscribed(false);
            }
          } catch (err) {
            console.error("Fallback query failed, likely due to rules:", err);
            setAlreadySubscribed(false);
          }
        } catch (err) {
          console.error("Error checking subscription:", err);
        }
      } else {
        setAlreadySubscribed(false);
      }
    }
    checkSubscription();
  }, [currentUser]);

  async function handleSubmit(e) {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;

    setSaving(true);
    try {
      const docRef = doc(db, "subscribers", trimmed);
      
      let exists = false;
      try {
        const docSnap = await getDoc(docRef);
        exists = docSnap.exists();
      } catch (readErr) {
        // Ignore read errors that might stem from Firestore rules
      }

      if (exists) {
        toast.info("You have already subscribed!");
        setAlreadySubscribed(true);
        setSaving(false);
        return;
      }

      await setDoc(docRef, {
        email: trimmed,
        subscribedAt: serverTimestamp(),
      });
      
      setSubmitted(true);
      setEmail("");
      toast.success("Successfully subscribed!");
    } catch (err) {
      console.error("Subscribe error:", err);
      // If writing fails with permissions, the rule likely blocks updates (already subscribed)
      if (err.message && err.message.includes("permissions")) {
        toast.info("You have already subscribed!");
        setAlreadySubscribed(true);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="py-16 px-4">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
          className="rounded-3xl overflow-hidden relative text-center px-6 py-10 sm:px-8 sm:py-14 mx-4 sm:mx-0"
          style={{ background: "linear-gradient(135deg, #ff6b9d 0%, #c44dff 50%, #ff8e53 100%)" }}
        >
          <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-white/10 -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full bg-white/10 translate-y-1/2 -translate-x-1/2" />

          <div className="relative">
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="flex justify-center mb-5"
            >
              <MailOutlineIcon sx={{ fontSize: 52, color: "white" }} />
            </motion.div>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3">
              {alreadySubscribed ? "You're All Set!" : "Get Weekly Deals & New Arrivals"}
            </h2>
            <p className="text-white/80 text-sm sm:text-base mb-8 max-w-lg mx-auto">
              {alreadySubscribed
                ? "You are currently subscribed to our newsletter. We'll keep sending you the best deals and curated picks straight to your inbox."
                : "Join 50,000+ happy shoppers. Get exclusive deals, new arrivals, and curated picks straight to your inbox."}
            </p>

            <AnimatePresence mode="wait">
              {alreadySubscribed ? (
                <motion.div
                  key="already-subscribed"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white font-semibold px-8 py-3.5 rounded-2xl shadow-lg border border-white/20 text-sm"
                >
                  <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />
                  Subscribed to Newsletter
                </motion.div>
              ) : submitted ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="inline-flex items-center gap-2 bg-white text-emerald-700 font-semibold px-8 py-3.5 rounded-2xl shadow-lg text-sm"
                >
                  <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />
                  You're in! Watch your inbox
                </motion.div>
              ) : (
                <motion.form key="form" onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                  <input
                    type="email" required
                    placeholder="Enter your email…"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 px-5 py-3.5 rounded-2xl text-sm text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-white/50 focus:outline-none shadow-lg"
                  />
                  <motion.button
                    whileTap={{ scale: 0.95 }} type="submit" disabled={saving}
                    className="bg-white text-rose-600 font-bold px-7 py-3.5 rounded-2xl hover:bg-white/90 transition-colors shadow-lg text-sm whitespace-nowrap disabled:opacity-70"
                  >
                    {saving ? "Saving…" : "Subscribe Free"}
                  </motion.button>
                </motion.form>
              )}
            </AnimatePresence>
            {!alreadySubscribed && (
              <p className="text-white/60 text-xs mt-4">No spam, unsubscribe any time.</p>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
export default function Home() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const productsSectionRef = useRef(null);

  // URL params
  const selectedCategory = searchParams.get("category") || "All";
  const searchQuery = searchParams.get("search") || "";
  const occasionFilter = searchParams.get("occasion") || "";
  const minPrice = parseFloat(searchParams.get("minPrice") || "0");
  const maxPrice = parseFloat(searchParams.get("maxPrice") || "999999");

  // ── Fetch products ───────────────────────────────────────────────────────
  useEffect(() => {
    async function fetchProducts() {
      try {
        setLoading(true);
        const q = query(collection(db, "products"), orderBy("createdAt", "desc"));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          await seedProducts();
          const seeded = await getDocs(q);
          setProducts(seeded.docs.map((d) => ({ id: d.id, ...d.data() })));
        } else {
          setProducts(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        }
      } catch (err) {
        console.error("Error fetching products:", err);
        setError("Failed to load products. Please check your Firebase configuration.");
      } finally {
        setLoading(false);
      }
    }
    fetchProducts();
  }, []);

  // ── Filter products ──────────────────────────────────────────────────────
  const filtered = products.filter((p) => {
    const matchCat = selectedCategory === "All" || p.category === selectedCategory;
    const matchSearch = !searchQuery ||
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchOccasion = !occasionFilter ||
      (Array.isArray(p.tags) && p.tags.includes(occasionFilter));
    const matchPrice = p.price >= minPrice && p.price <= maxPrice;
    return matchCat && matchSearch && matchOccasion && matchPrice;
  });

  // Derived product sets (homepage sections use all products unfiltered)
  const trending = products.slice(0, 4);
  const featured = products.slice(0, 8);

  // Scroll to products section whenever a filter is applied
  useEffect(() => {
    if (isFiltered && productsSectionRef.current) {
      setTimeout(() => {
        productsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [searchParams.toString()]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleCategorySelect(cat) {
    setSearchParams(cat === "All" ? {} : { category: cat });
  }

  // Show filtered result section when any filter is active
  const isFiltered = selectedCategory !== "All" || searchQuery || occasionFilter ||
    searchParams.has("minPrice") || searchParams.has("maxPrice");

  // ── Active filter label ──────────────────────────────────────────────────
  function activeFilterLabel() {
    if (searchQuery) return `Results for "${searchQuery}"`;
    if (occasionFilter) return `${occasionFilter.charAt(0).toUpperCase() + occasionFilter.slice(1)} Gifts`;
    if (searchParams.has("minPrice")) {
      const lo = searchParams.get("minPrice");
      const hi = searchParams.get("maxPrice");
      const hiLabel = parseFloat(hi) >= 9999 ? "$100+" : `$${hi}`;
      return `Gifts $${lo} – ${hiLabel}`;
    }
    return selectedCategory;
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="page-enter" style={{ backgroundColor: "#FFFBF8" }}>

      {/* Hero Slider */}
      <HeroSlider />

      {/* Trust Badges */}
      <TrustBadges />

      {/* Occasion Categories */}
      <OccasionCategories />

      {/* ── Trending Gifts ──────────────────────────────────────────────── */}
      {!isFiltered && (
        <section className="py-16 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="relative mb-8 sm:mb-10">
              <SectionHeader
                badge="Hot Right Now"
                BadgeIcon={LocalFireDepartmentOutlinedIcon}
                title="Trending"
                accentTitle="Gifts"
                desc="Most-loved picks this week"
                badgeColor="text-orange-600 bg-orange-50"
              />
              <Link
                to="/products"
                className="hidden sm:flex absolute right-0 top-0 items-center gap-1 text-sm font-semibold text-rose-600 hover:text-rose-700 transition-colors mt-2 flex-shrink-0"
              >
                View All <ArrowForwardIcon sx={{ fontSize: 16 }} />
              </Link>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {Array.from({ length: 4 }).map((_, i) => <ProductSkeleton key={i} />)}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {trending.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Price Range Section */}
      {!isFiltered && <PriceRangeSection />}

      {/* ── Featured Collection ──────────────────────────────────────────── */}
      {!isFiltered && (
        <section className="py-16 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <SectionHeader
              badge="Curated For You"
              BadgeIcon={StarOutlinedIcon}
              title="Featured"
              accentTitle="Collection"
              desc="Handpicked by our gifting experts"
            />

            {/* Category filter pills */}
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              {CATEGORIES.map(({ label, Icon, color }) => (
                <motion.button
                  key={label}
                  whileTap={{ scale: 0.94 }}
                  onClick={() => handleCategorySelect(label)}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all border
                    ${selectedCategory === label
                      ? `bg-gradient-to-r ${color} text-white border-transparent shadow-gift-sm`
                      : "bg-white text-gray-700 border-gray-200 hover:border-rose-200 hover:text-rose-600 hover:bg-rose-50"
                    }`}
                >
                  <Icon sx={{ fontSize: 16 }} />
                  {label}
                </motion.button>
              ))}
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {Array.from({ length: 8 }).map((_, i) => <ProductSkeleton key={i} />)}
              </div>
            ) : error ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="bg-rose-50 border border-rose-200 text-rose-700 rounded-2xl p-8 text-center"
              >
                <ErrorOutlineIcon sx={{ fontSize: 40, marginBottom: 1 }} />
                <p className="font-semibold text-lg mb-1">Something went wrong</p>
                <p className="text-sm opacity-80">{error}</p>
              </motion.div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Filtered Results ─────────────────────────────────────────────── */}
      {isFiltered && (
        <section ref={productsSectionRef} className="py-16 px-4 bg-white">
          <div className="max-w-7xl mx-auto">

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 text-center sm:text-left">
              <div>
                <h2 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">
                  <span className="text-gradient-gift">{activeFilterLabel()}</span>
                </h2>
                <p className="text-gray-500 text-sm mt-1">
                  {loading ? "Loading…" : `${filtered.length} item${filtered.length !== 1 ? "s" : ""} found`}
                </p>
              </div>

              {/* Category pills */}
              <div className="flex flex-wrap justify-center sm:justify-start gap-2">
                {CATEGORIES.map(({ label, Icon, color }) => (
                  <button
                    key={label}
                    onClick={() => handleCategorySelect(label)}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all border
                      ${selectedCategory === label && !occasionFilter && !searchParams.has("minPrice")
                        ? "bg-rose-500 text-white border-transparent shadow-gift-sm"
                        : "bg-white text-gray-600 border-gray-200 hover:border-rose-300 hover:text-rose-600"
                      }`}
                  >
                    <Icon sx={{ fontSize: 13 }} /> {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Grid */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {Array.from({ length: 8 }).map((_, i) => <ProductSkeleton key={i} />)}
              </div>
            ) : filtered.length === 0 ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-24">
                <SearchOffIcon sx={{ fontSize: 56, color: "#d1d5db", marginBottom: 2 }} />
                <p className="font-display text-xl sm:text-2xl font-bold text-gray-900 mb-2">No gifts found</p>
                <p className="text-gray-500 mb-6">Try a different filter or browse all categories</p>
                <button
                  onClick={() => setSearchParams({})}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-rose-500 to-pink-600 text-white font-semibold px-6 py-3 rounded-2xl hover:opacity-90 transition shadow-gift-sm"
                >
                  Browse All Products
                </button>
              </motion.div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filtered.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Promo Banners ────────────────────────────────────────────────── */}
      {!isFiltered && (
        <section className="py-10 px-4" style={{ background: "linear-gradient(180deg, #ffffff 0%, #fff8f0 100%)" }}>
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">

              {/* Large dark card */}
              <div
                className="md:col-span-2 rounded-3xl overflow-hidden relative min-h-48"
                style={{ background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)" }}
              >
                <div className="absolute top-0 right-0 w-48 h-48 rounded-full bg-rose-500/20 blur-2xl" />
                <div className="relative p-6 sm:p-8 md:p-10 flex flex-col justify-between h-full text-center sm:text-left">
                  <div>
                    <span className="inline-block text-xs font-bold uppercase tracking-widest text-rose-400 bg-rose-400/20 px-3 py-1 rounded-full mb-4">
                      New This Week
                    </span>
                    <h3 className="font-display text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2">
                      Personalized Products <br />
                      <span className="text-gradient-gift">Just For You</span>
                    </h3>
                    <p className="text-white/60 text-sm">
                      Add a personal touch to any order. Names, dates, messages — all on demand.
                    </p>
                  </div>
                  <Link
                    to="/?category=Gift Items"
                    className="mt-5 sm:mt-6 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-rose-500 to-pink-600 text-white font-bold px-5 py-2.5 sm:px-6 sm:py-3 rounded-xl hover:opacity-90 transition shadow-gift text-xs sm:text-sm self-center sm:self-start"
                  >
                    Explore Now <ArrowForwardIcon sx={{ fontSize: 16 }} />
                  </Link>
                </div>
              </div>

              {/* Two small promo cards */}
              <div className="flex flex-col gap-5">
                <Link
                  to="/?category=Toys"
                  className="group rounded-3xl overflow-hidden relative flex-1 min-h-32"
                  style={{ background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)" }}
                >
                  <div className="absolute inset-0 flex items-center justify-end pr-6 pointer-events-none opacity-20 group-hover:opacity-30 transition-opacity">
                    <ToysOutlinedIcon sx={{ fontSize: 72, color: "#92400e" }} />
                  </div>
                  <div className="relative p-6 text-center sm:text-left">
                    <p className="text-xs font-bold uppercase tracking-widest text-amber-900/70 mb-1">Toys & Games</p>
                    <p className="font-display text-xl font-bold text-amber-900">Fun for All Ages</p>
                    <p className="text-amber-800/70 text-xs mt-1 flex items-center justify-center sm:justify-start gap-1">
                      Explore <ArrowForwardIcon sx={{ fontSize: 12 }} />
                    </p>
                  </div>
                </Link>

                <Link
                  to="/?category=Handicrafts"
                  className="group rounded-3xl overflow-hidden relative flex-1 min-h-32"
                  style={{ background: "linear-gradient(135deg, #6ee7b7 0%, #059669 100%)" }}
                >
                  <div className="absolute inset-0 flex items-center justify-end pr-6 pointer-events-none opacity-20 group-hover:opacity-30 transition-opacity">
                    <BrushOutlinedIcon sx={{ fontSize: 72, color: "#064e3b" }} />
                  </div>
                  <div className="relative p-6 text-center sm:text-left">
                    <p className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-1">Handicrafts</p>
                    <p className="font-display text-xl font-bold text-emerald-900">Artisan Crafted</p>
                    <p className="text-emerald-800/70 text-xs mt-1 flex items-center justify-center sm:justify-start gap-1">
                      Explore <ArrowForwardIcon sx={{ fontSize: 12 }} />
                    </p>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Newsletter */}
      <NewsletterSection />
    </div>
  );
}
